import { Component, Input, Output, EventEmitter, computed, signal, TemplateRef } from '@angular/core';
import { IEntity } from '../../../_models/IEntity';
import { MenuItemObject } from '../../models/context-menu.models';
import { ContextMenuComponent } from '../../nested-components/context-menu/context-menu.component';
import { IEntityMetaData, IMetaProprety, IPropretyAttribute } from '../../../_models/IEntityMetaData';
import { ColumnType } from '../../models/components.models';
import { ListTableColumnComponent } from '../list-table-column/list-table-column.component';
import { Decorators } from '../../../decorators/EntityDecorators';
import { EntityBase } from '../../../_models/EntityBase';
import { TranslationService } from '../../../services/TranslationService';
import { TranslatePipe } from '../../directives/TranslatePipe';
import { EntityState, IEntityState } from '../../../_models/IChangedEntity';
import { RichTooltipDirective, TooltipEntityContext } from '../../directives/Tooltip.directive';
import { Form } from '../../../models/matwins-tenant/Form.model';
import { CommonModule } from '@angular/common';
import { GenericComponent } from '../../_base/GenericComponent';
import { TimeZoneIconComponent } from '../../nested-components/timezone-icon/timezone-icon.component';

@Component({
  selector: 'app-table',
  templateUrl: './list-table.component.html',
  standalone: true,
  imports: [CommonModule, ContextMenuComponent, TranslatePipe, RichTooltipDirective, TimeZoneIconComponent]
})
export class TableComponent<T extends IEntity & IEntityState> {
  private metaData!: IEntityMetaData;

  @Input() entities: T[] = [];
  @Input() columns: ListTableColumnComponent[] = [];

  // Fuseau IANA par site, aux seuls sites dont le decalage differe ; ne sert qu'a l'icone.
  @Input() fuseauxParSite: Map<number, string> = new Map();
  @Input() sortableColumns: string[] | null = [];
  @Input() sortBy = '';
  @Input() sortOrder: 'asc' | 'desc' = 'asc';
  @Input() selectedId: number | string | null = null;
  @Input() contextMenuItems: MenuItemObject<T>[] = [];
  @Input() entityType!: new () => T;
  @Input() showActions: boolean = true;
  @Input() canEdit: (item: T) => boolean = () => true;

  @Input() dismissEntity?: (entity: any) => void;
  @Input() tooltipTemplate!: TemplateRef<any>;

  // Outputs
  @Output() sortChange = new EventEmitter<{ sortBy: string, sortOrder: 'asc' | 'desc' }>();
  @Output() entitySelected = new EventEmitter<number | string | null>();
  @Output() contextMenuAction = new EventEmitter<{ action: string, entity: T }>();
  @Output() editAction = new EventEmitter<T>();
  @Output() deleteAction = new EventEmitter<T>();

  @Input() showEntityState: boolean = false;

  public EntityState = EntityState; // pour utilisation dans le template

  private dismissTimers = new Map<string | number, any>();

  // visible="sm".."2xl" : la colonne est masquee sous ce breakpoint.
  // Les classes doivent rester en litteral pour le scan Tailwind.
  private readonly responsiveVisibilityClasses: Record<string, string> = {
    sm: 'hidden sm:table-cell',
    md: 'hidden md:table-cell',
    lg: 'hidden lg:table-cell',
    xl: 'hidden xl:table-cell',
    '2xl': 'hidden 2xl:table-cell',
  };

  visibilityClass(col: ListTableColumnComponent): string {
    return this.responsiveVisibilityClasses[col.visible] ?? '';
  }


  constructor(private translationService: TranslationService) { }


  // Signaux pour le menu contextuel
  contextMenuVisible = signal(false);
  contextMenuPosition = signal({ x: 0, y: 0 });
  contextMenuEntity = signal<T | null>(null);

  effectiveMenuItems = computed(() => {
    const entity = this.contextMenuEntity();
    if (!entity) return [];

    return this.contextMenuItems.map(item => ({
      itemKey: item.itemKey,
      itemText: item.itemText,
      icon: item.icon,
      action: () => this.handleContextMenuAction(item.itemKey, entity),
      disabled: item.disabled ? item.disabled(entity) : false
    }));
  });

  dataColumns = computed(() => {
    // Colonnes de base (soit les colonnes fournies, soit générées à partir des métadonnées)
    let baseColumns = this.columns.length ? this.columns : this.generateColumnsFromMetadata();

    // S'assurer que les templates sont correctement attachés pour les colonnes personnalisées
    baseColumns.forEach(col => {
      if (col.type === 'custom' && col.getTemplate && col.getTemplate()) {
        col.customTemplate = col.getTemplate();
      }
    });

    return baseColumns;
  });

  private generateColumnsFromMetadata(): ListTableColumnComponent[] {
    if (!this.metaData && this.entities.length && this.entityType) {
      this.metaData = {} as IEntityMetaData;
    }
    if (!this.metaData) return [];

    return this.metaData.PropertyNames.map(prop => {
      const column = new ListTableColumnComponent();
      column.key = prop;
      column.title = this.formatTitle(prop);
      column.type = this.determineColumnType(prop);
      column.sortable = EntityBase.CheckPropretyHasDecoratorFromMeta(this.metaData, prop, IPropretyAttribute.SortColumn);
      return column;
    });
  }

  private formatTitle(key: string): string {
    return key
      .replace(/([A-Z])/g, ' $1')
      .replace(/^./, str => str.toUpperCase())
      .trim();
  }

  private determineColumnType(key: string): ColumnType {
    const propType = Reflect.getMetadata('design:type', this.metaData, key);
    if (propType === Date) return 'date';
    if (propType === Boolean) return 'boolean';
    if (key.toLowerCase().includes('progress')) return 'progress';
    return 'text';
  }



  // Fuseau du site DE CETTE LIGNE, pas celui selectionne dans la navigation.
  fuseauDeLigne(entity: T): string | null {
    const fk = (entity as any)?.fK_PointOfService;
    if (fk == null) return null;
    return this.fuseauxParSite.get(Number(fk)) ?? null;
  }

  getFormatedDateValue(value: Date | string | null | undefined) {
    return GenericComponent.GetFormatedDateValue(value, this.translationService.instant('app.short-local-date-time'), this.translationService.instant('app.time-output-format'));
  }
  getFormatedDateTimeValue(value: Date | string | null | undefined) {
    let uuu = this.translationService.instant('app.short-local-date-time')

    return GenericComponent.GetFormatedDateTimeValue(value, this.translationService.instant('app.short-local-date-time'), this.translationService.instant('app.time-output-format'));
  }

  sort(column: string) {
    if (this.sortableColumns?.includes(column)) {
      if (this.sortBy === column) {
        const newSortOrder = this.sortOrder === 'asc' ? 'desc' : 'asc';
        this.sortChange.emit({ sortBy: column, sortOrder: newSortOrder });
      } else {
        this.sortChange.emit({ sortBy: column, sortOrder: 'asc' });
      }
    }
  }

  onRightClick(event: MouseEvent, entity: T) {
    event.preventDefault();
    if (this.contextMenuItems.length == 0)
      return;

    this.contextMenuEntity.set(entity);
    this.contextMenuPosition.set({ x: event.clientX, y: event.clientY });
    this.contextMenuVisible.set(true);
  }

  hideContextMenu() {
    this.contextMenuVisible.set(false);
  }

  isSelected(entity: T) {
    return this.selectedId === entity.id;
  }

  // Les entites inactives restent visibles mais s'affichent estompees.
  isInactive(entity: T): boolean {
    return (entity as any)?.isActive === false;
  }

  selectItem(id: number) {
    if (this.selectedId === id) {
      this.entitySelected.emit(null);
    }
    else {
      this.entitySelected.emit(id);
    }
  }

  handleContextMenuAction(action: string, entity: T) {
    this.contextMenuAction.emit({ action, entity });
    this.hideContextMenu();
  }

  onEdit(entity: T, event: Event) {
    event.stopPropagation();
    this.editAction.emit(entity);
  }

  onDelete(entity: T, event: Event) {
    event.stopPropagation();
    this.deleteAction.emit(entity);
  }


  ngOnDestroy(): void {
    this.dismissTimers.forEach(timer => clearTimeout(timer));
    this.dismissTimers.clear();
  }

  // Helper to get nested property
  getNestedValue(obj: any, key: string): any {
    return key.split('.').reduce((o, k) => {
      if (o == null) return undefined;
      // Gère la notation avec crochets : prop[0] → prop puis index 0
      const match = k.match(/^(.+)\[(\d+)\]$/);
      if (match) {
        const arr = o[match[1]];
        const idx = parseInt(match[2], 10);
        return arr && arr[idx] !== undefined ? arr[idx] : undefined;
      }
      return o[k] !== undefined ? o[k] : undefined;
    }, obj);
  }

  getTooltipContext(entity: T): TooltipEntityContext {
    return {
      entity: entity,
      dismiss: () => this.dismissEntity ? this.dismissEntity(entity) : () => { }
    };
  }

  getStripeGradient(state: EntityState | undefined): string {
    if (!state || state === EntityState.Unchanged) return 'none';
    switch (state) {
      case EntityState.Added:
        return 'repeating-linear-gradient(-45deg, #f0fdf4 0px, #f0fdf4 4px, #bbf7d0 4px, #bbf7d0 8px)';
      case EntityState.Modified:
        return 'repeating-linear-gradient(-45deg, #faf5ff 0px, #faf5ff 4px, #e9d5ff 4px, #e9d5ff 8px)';
      case EntityState.Deleted:
        return 'repeating-linear-gradient(-45deg, #fef2f2 0px, #fef2f2 4px, #fecaca 4px, #fecaca 8px)';
      default:
        return 'none';
    }
  }

  startAutoDismissTimer(entity: T): void {
    this.cancelAutoDismissTimer(entity);
    const timer = setTimeout(() => {
      this.dismissEntity?.(entity);
      this.dismissTimers.delete(entity.id);
    }, 3000);
    this.dismissTimers.set(entity.id, timer);
  }

  cancelAutoDismissTimer(entity: T): void {
    const timer = this.dismissTimers.get(entity.id);
    if (timer) {
      clearTimeout(timer);
      this.dismissTimers.delete(entity.id);
    }
  }
}
